<template>
  <div class="policy-service" :class="[
    {
      'animate__animated animate__zoomOutLeft':
        serviceIsDeleting === service.id,
    },
    { deleting: isDeleted },
  ]">
    <Loading v-if="serviceLoading" />
    <template v-else>
      <div class="policy-service__row">
        <div class="policy-service__data policy-service__data--type">
          {{ code }} <template v-if="mandate"> & {{ mandate.service.code }}</template>
        </div>

        <div class="policy-service__data"
          :class="{ 'policy-service__data--underline': $checkRolePermission('policy_service_edit') }">
          <span v-if="$checkRolePermission('policy_service_edit')" @click="showStartDatePopup = true"
            style="cursor: pointer">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showStartDatePopup" :isDate="true" @togglePopup="showStartDatePopup = $event"
                :selected="unformattedStartDate" @submit="updatePolicyServiceStartDate($event)" />
            </transition>
            {{ startDate }}
          </span>
          <span v-else>
            {{ startDate }}
          </span>
        </div>

        <div class="policy-service__data"
          :class="{ 'policy-service__data--underline': code !== 'KVG' && $checkRolePermission('policy_service_edit') }">
          <span v-if="code !== 'KVG' && $checkRolePermission('policy_service_edit')" @click="showEndDatePopup = true"
            style="cursor: pointer">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showEndDatePopup" :isDate="true" @togglePopup="showEndDatePopup = $event"
                :selected="unformattedEndDate" @submit="updatePolicyServiceEndDate($event)" />
            </transition>
            {{ endDate }}
          </span>
          <span v-else>
            {{ endDate }}
          </span>
        </div>

        <div class="policy-service__data">{{ dueDate }}</div>
        <div class="policy-service__data">
          <span v-if="owner">{{ owner.first_name }} {{ owner.last_name }}</span>
          <span v-else>----------</span>
        </div>
        <div class="policy-service__data service__data--price"
          :class="{ 'policy-service__data--netto': isLastService }">
          <div>{{ nettoPremium }}</div>
          <div class="policy-service__netto" v-if="isLastService">
            <span>Total:</span>{{ totalNetto.toFixed(2) }}
          </div>
        </div>
        <div class="policy-service__data policy-service__data--price" :class="{
          'policy-service__data--underline': $checkRolePermission('policy_service_edit'),
          'policy-service__data--brutto': isLastService
        }">
          <span v-if="$checkRolePermission('policy_service_edit')" @click="showBruttoPremiumPopup = true"
            style="cursor: pointer">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showBruttoPremiumPopup" :isDate="false" @togglePopup="showBruttoPremiumPopup = $event"
                :selected="service.bruto_premium" @submit="updatePolicyServiceBruttoPremium($event)" />
            </transition>
            {{ bruttoPremium }}
          </span>

          <span v-else>
            {{ bruttoPremium }}
          </span>

          <span class="policy-service__brutto" v-if="isLastService">
            {{ totalBrutto }} .-CHF
          </span>
        </div>

        <div class="policy-service__data"
          :class="{ 'policy-service__data--underline': $checkRolePermission('policy_service_edit') }">
          <span v-if="$checkRolePermission('policy_service_edit')" @click="showIntervalPopup = true"
            style="cursor: pointer">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showIntervalPopup" @togglePopup="showIntervalPopup = $event" :items="intervals"
                :selected="premiumInterval" label="name" :position="'left'"
                @submit="updatePolicyServiceInterval($event)" />
            </transition>
            {{ premiumInterval }}
          </span>

          <span v-else>
            {{ premiumInterval }}
          </span>
        </div>

        <div class="policy-service__data policy-service__data--payment">
          <div v-if="$checkRolePermission('policy_service_payment_access')" class="icon icon__payment" :class="{
            'icon__payment--paid': isPaid,
            'icon__payment--pending': isPaymentEntered && !isPaid
          }" @click="openPolicyProvisionModal()"></div>
        </div>

        <div class="policy-service__data policy-service__data--delete">
          <div v-if="$checkRolePermission('policy_service_delete')" class="icon icon__delete"
            :class="{ 'icon__delete--active': showDeletePopup, 'is-paid': isPaid }" tabindex="-1"
            @focus="!isPaid && (showDeletePopup = true)" @focusout="!isPaid && (showDeletePopup = false)" ref="delete">
            <transition name="fade">
              <div class="delete" v-if="showDeletePopup">
                <div class="delete__text">
                  Are you sure you want to delete this Service?
                </div>
                <div class="delete__buttons">
                  <div class="button button--confirm" @click="deleteService()"></div>
                  <div class="button button--cancel" @click="closeDeletePopup()"></div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
      <div class="policy-service__name">{{ name }} </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import moment from "moment";
import statics from "@/statics";
import PopupEditer from "@/partials/PopupEditer";
import Loading from "@/partials/Loading";

defineOptions({
  name: "PolicyService"
});

const props = defineProps({
  service: { required: true, type: Object },
  fetchGlobalCustomer: { required: true, type: Function },
  isLastService: { required: true, type: Boolean },
  totalNetto: { required: true, type: Number },
  totalBrutto: { required: true, type: Number },
  mandate: { type: Object, required: false },
  owner: { required: true },
  createdAt: { required: true, type: String },
});

const store = useStore();
const route = useRoute();

// State
const showIntervalPopup = ref(false);
const showStartDatePopup = ref(false);
const showEndDatePopup = ref(false);
const serviceLoading = ref(false);
const serviceIsDeleting = ref("");
const isDeleted = ref(false);
const showDeletePopup = ref(false);
const showBruttoPremiumPopup = ref(false);

// Template Refs
const deleteRef = ref(null);

// Computed
const language = computed(() => store.getters.language);
const policyForms = computed(() => store.getters.policyForms);

const code = computed(() => props.service.service?.code || statics.empty);

const unformattedStartDate = computed(() => props.service.start_date || statics.empty);
const unformattedEndDate = computed(() => props.service.end_date || statics.empty);

const startDate = computed(() =>
  props.service.start_date
    ? moment(props.service.start_date.split("T")[0]).format("DD.MM.YYYY")
    : statics.empty
);

const endDate = computed(() =>
  props.service.end_date
    ? moment(props.service.end_date.split("T")[0]).format("DD.MM.YYYY")
    : statics.empty
);

const nettoPremium = computed(() => props.service.netto_premium ? `${props.service.netto_premium} .-CHF` : statics.empty);
const bruttoPremium = computed(() => props.service.bruto_premium ? `${props.service.bruto_premium} .-CHF` : statics.empty);
const premiumInterval = computed(() => props.service.premium_interval?.name || statics.empty);

const dueDate = computed(() => statics.empty);

const name = computed(() => {
  if (props.service.service_model) return props.service.service_model.name;
  return props.service.service ? props.service.service[`long_${language.value}`] || statics.empty : statics.empty;
});

//const plateNumber = computed(() => props.service.plate_number || statics.empty);
const intervals = computed(() => policyForms.value.intervals);

const isPaid = computed(() =>
  props.service?.policy_service_payments?.length > 0
    ? props.service.policy_service_payments.every(p => p.paid === true)
    : false
);

const isPaymentEntered = computed(() =>
  props.service?.policy_service_payments?.length > 0
    ? props.service.policy_service_payments.every(p => p !== null && p !== undefined)
    : false
);

// Methods
const closeDeletePopup = () => {
  deleteRef.value?.blur();
  showDeletePopup.value = false;
};

const deleteService = async () => {
  const { id } = route.params;
  serviceIsDeleting.value = props.service.id;
  closeDeletePopup();
  try {
    await store.dispatch("deletePolicyService", props.service.id);
    isDeleted.value = true;
    setTimeout(async () => {
      await store.dispatch("getGlobalCustomer", id);
    }, 300);
  } catch (error) {
    // Error already handled by store action
    serviceIsDeleting.value = "";
  }
};

const updatePolicyServiceInterval = async (intervalId) => {
  serviceLoading.value = true;
  try {
    await store.dispatch("updatePolicyServiceInterval", {
      serviceId: props.service.id,
      intervalId,
    });
    await props.fetchGlobalCustomer(route.params.id);
    showIntervalPopup.value = false;
  } catch (error) {
    // Error already handled by store action
  } finally {
    serviceLoading.value = false;
  }
};

const updatePolicyServiceStartDate = async (date) => {
  serviceLoading.value = true;
  try {
    await store.dispatch("updatePolicyServiceStartDate", {
      serviceId: props.service.id,
      date,
    });
    await props.fetchGlobalCustomer(route.params.id);
    showStartDatePopup.value = false;
  } catch (error) {
    // Error already handled by store action
  } finally {
    serviceLoading.value = false;
  }
};

const updatePolicyServiceBruttoPremium = async (bruttoPremium) => {
  serviceLoading.value = true;
  try {
    await store.dispatch("updatePolicyServiceBruttoPremium", {
      serviceId: props.service.id,
      bruttoPremium,
    });
    await props.fetchGlobalCustomer(route.params.id);
    showBruttoPremiumPopup.value = false;
  } catch (error) {
    // Error already handled by store action
  } finally {
    serviceLoading.value = false;
  }
};

const updatePolicyServiceEndDate = async (date) => {
  serviceLoading.value = true;
  try {
    await store.dispatch("updatePolicyServiceEndDate", {
      serviceId: props.service.id,
      date,
    });
    await props.fetchGlobalCustomer(route.params.id);
    showEndDatePopup.value = false;
  } catch (error) {
    // Error already handled by store action
  } finally {
    serviceLoading.value = false;
  }
};

const openPolicyProvisionModal = () => {
  store.commit("setSelectedPolicyService", props.service);
  store.commit("setShowPolicyProvisionModal", true);
  store.commit("setPolicyCreatedAt", props.createdAt);
};
</script>

<style scoped>
.is-paid {
  cursor: not-allowed;
}
</style>
